import { useState, useMemo } from 'react';

interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

/**
 * Hook per gestire l'ordinamento di una lista di dati.
 * 
 * @param {Array} data - Array di oggetti da ordinare
 * @param {Object} config - Configurazione iniziale { key: string, direction: 'asc' | 'desc' }
 * @returns {Object} - { sortedData, sortConfig, requestSort, getSortIcon }
 * 
 * Esempio di utilizzo:
 * const { sortedData, sortConfig, requestSort, getSortIcon } = useSortableData(products, { key: 'name', direction: 'asc' });
 * 
 * <TableHead onClick={() => requestSort('name')} className="cursor-pointer">
 *   Nome {getSortIcon('name')}
 * </TableHead>
 */
export const useSortableData = <T extends Record<string, any>>(
  data: T[] | undefined | null, 
  config: SortConfig | null = null
) => {
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(config);

  const sortedData = useMemo(() => {
    if (!data) return [];
    
    let sortableItems = [...data];
    
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        // Gestione valori null/undefined
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;
        
        // Confronto per tipo
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          const comparison = aValue.localeCompare(bValue, 'it', { sensitivity: 'base' });
          return sortConfig.direction === 'asc' ? comparison : -comparison;
        }
        
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }
        
        // Confronto date
        if (aValue instanceof Date || (typeof aValue === 'string' && !isNaN(Date.parse(aValue)))) {
          const dateA = new Date(aValue);
          const dateB = new Date(bValue);
          return sortConfig.direction === 'asc' 
            ? dateA.getTime() - dateB.getTime() 
            : dateB.getTime() - dateA.getTime();
        }
        
        // Fallback generico
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return sortableItems;
  }, [data, sortConfig]);

  const requestSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key: string) => {
    if (!sortConfig || sortConfig.key !== key) {
      return '↕';
    }
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const getSortClass = (key: string) => {
    if (!sortConfig || sortConfig.key !== key) {
      return 'text-muted-foreground/50';
    }
    return 'text-primary';
  };

  return { sortedData, sortConfig, requestSort, getSortIcon, getSortClass };
};

export default useSortableData;
